"use client";

/**
 * Is there room for a dialog, or is this a phone?
 *
 * The design system's `Sheet` already records the decision this answers: a
 * sheet is the phone presentation of a task that suspends the page, and
 * `Modal` is the same task past rail width — one decision with two shapes. It
 * did not ship the thing that picks between them, so every caller was going to
 * invent its own, and two of them disagreeing by a pixel is a surface that is
 * a sheet and a dialog at the same width.
 *
 * A QUERY, NOT A CLASS. The usual way to answer this in a page is `lg:hidden`
 * on one tree and `hidden lg:block` on the other, and it does not work for an
 * overlay: both trees stay mounted, `display: none` does not reach portalled
 * content, and two open overlays mean two focus traps fighting over the
 * keyboard. The branch has to happen in JavaScript so only one of them exists.
 *
 * Phone-first before hydration, because that is the layout that fits
 * anywhere — a sheet rendered briefly at desk width is a panel in the wrong
 * place, while a modal rendered briefly on a phone can overflow the screen.
 */

import { useCallback, useSyncExternalStore } from "react";

/** Tailwind's `lg`, which is the portal's rail width. */
const WIDE = "(min-width: 1024px)";

export function useIsWide(query: string = WIDE): boolean {
  const subscribe = useCallback(
    (onChange: () => void) => {
      const mql = window.matchMedia(query);
      mql.addEventListener("change", onChange);
      return () => mql.removeEventListener("change", onChange);
    },
    [query],
  );

  return useSyncExternalStore(
    subscribe,
    () => window.matchMedia(query).matches,
    () => false,
  );
}
